import React, { useEffect, useState } from 'react';
import { Table, TextInput, rem, createStyles, UnstyledButton, Group, Text, Center, Pagination, Flex, ScrollArea, Skeleton } from '@mantine/core';
import { IconChevronUp, IconChevronDown, IconSelector, IconSearch } from '@tabler/icons-react';
import { keys } from '@mantine/utils';
import { notifications } from '@mantine/notifications';
import { getCoupons } from '../../../services/api.service';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: theme.spacing.md,
		height: '100%',
	},
	th: {
		padding: '0 !important',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.md}`,
		'&:hover': {
			backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
		},
	},
	icon: {
		width: rem(21),
		height: rem(21),
		borderRadius: rem(21),
	},
}));

const CouponsTab = () => {

	// State and hooks
	const [isLoading, setIsLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [coupons, setCoupons] = useState([]);
	const [sortedData, setSortedData] = useState([]);
	const [sortBy, setSortBy] = useState(null);
	const [reverseSortDirection, setReverseSortDirection] = useState(false);

	// On load
	useEffect(() => {
		// getCouponsList();
		// eslint-disable-next-line
	}, []);

	// Get coupons
	// eslint-disable-next-line
	const getCouponsList = () => {

		// Get
		getCoupons(function (response) {

			// Set coupons and initial sorted data, remove loader
			setCoupons(response.data);
			setSortedData(response.data);
			setIsLoading(false);

		}, function (error) {

			// Remove loader
			setIsLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// TH component
	const Th = ({ children, reversed, sorted, onSort }) => {
		const { classes } = useStyles();
		return (
			<th className={classes.th}>
				<UnstyledButton onClick={onSort} className={classes.control}>
					<Group position="apart">
						<Text fw={700} fz="sm">
							{children}
						</Text>
						<Center className={classes.icon}>
							{sorted ?
								(reversed ?
									<IconChevronUp size="0.9rem" stroke={1.5} />
									:
									<IconChevronDown size="0.9rem" stroke={1.5} />
								)
								: <IconSelector size="0.9rem" stroke={1.5} />}
						</Center>
					</Group>
				</UnstyledButton>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th
				sorted={sortBy === 'id'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('id')}
			>
				ID
			</Th>
			<Th
				sorted={sortBy === 'points'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('points')}
			>
				Puntos
			</Th>
		</tr>
	);

	// Build rows
	const rows = sortedData.map((element) => (
		<tr key={element.id}>
			<td width={100}>
				{element.id}
			</td>
			<td>
				{element.points}
			</td>
		</tr>
	));

	// Handle search change
	const handleSearchChange = (event) => {
		const { value } = event.currentTarget;
		setSearch(value);
		setSortedData(sortData(coupons, { sortBy, reversed: reverseSortDirection, search: value }));
	};

	// Get current sort
	const setSorting = (field) => {
		const reversed = field === sortBy ? !reverseSortDirection : false;
		setReverseSortDirection(reversed);
		setSortBy(field);
		setSortedData(sortData(coupons, { sortBy: field, reversed, search }));
	};

	// Filter and return from query/search/sort
	const filterData = (data, search) => {
		const query = search.toLowerCase().trim();
		return data.filter((item) => keys(data[0]).some((key) => item[key].toString().toLowerCase().includes(query)));
	}

	// Sort data
	const sortData = (data, payload) => {

		// Assign
		const { sortBy } = payload;

		// No filter
		if (!sortBy) { return filterData(data, payload.search); }

		// Return filtered data
		return filterData(
			[...data].sort((a, b) => {
				if (payload.reversed) {
					return b[sortBy].toString().localeCompare(a[sortBy]);
				}

				return a[sortBy].toString().localeCompare(b[sortBy]);
			}),
			payload.search
		);

	}

	return (
		<>
			{/* Content */}
			<Flex direction="column">
				{/* Top */}
				<Flex mb="md">
					{/* Search */}
					<TextInput
						placeholder="Buscar cupones"
						icon={<IconSearch size="0.9rem" stroke={1.5} />}
						value={search}
						onChange={handleSearchChange}
						sx={{
							width: '100%',
							maxWidth: 350
						}}
						variant="filled"
					/>
				</Flex>
				{/* Table */}
				<ScrollArea>
					{isLoading ?
						<>
							<Skeleton height={40} radius="sm" />
							<Skeleton height={32} mt={12} radius="sm" />
							<Skeleton height={32} mt={12} radius="sm" />
							<Skeleton height={32} mt={12} radius="sm" />
							<Skeleton height={32} mt={12} radius="sm" />
						</>
						:
						<Table
							highlightOnHover
							sx={{ flex: 2 }}
						>
							<thead>{ths}</thead>
							<tbody>{rows.length > 0 ? (
								rows
							) : (
								<tr>
									<td colSpan={7}>
										<Text
											weight={500}
											align="center"
											my="xl"
											color="dimmed"
										>
											Sin resultados
										</Text>
									</td>
								</tr>
							)}</tbody>
						</Table>
					}
				</ScrollArea>
			</Flex>
			{/* Pages */}
			<Center sx={{ marginTop: 20 }}>
				<Pagination
					total={1}
					siblings={1}
					defaultValue={1}
					withEdges
				/>
			</Center>
		</>
	);

}

export default CouponsTab;
